<script lang="ts" setup>
import { useMortality } from "@/composables/tasks/mortality-check/useMortality";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";
import { ref, computed } from "vue";
import { GroupTag, TankGroup } from "@/types/v2";

const { activeTank, navigateToOverview, navigateToGroupView } = useMortality();
const overview = ref(false);

const calculateDeceased = (progressData: any[]): number => {
    if (Array.isArray(progressData)) {
        return progressData.reduce(
            (acc, item) => acc + (item.quantity > 0 ? item.quantity : 0),
            0
        );
    }
    return 0;
};

const calculatePercentage = (progressData: any[] = [], total = 0): string => {
    if (Array.isArray(progressData)) {
        const totalDeceased = progressData.reduce(
            (acc, item) => acc + (item.quantity > 0 ? item.quantity : 0),
            0
        );

        if (total > 0) {
            return ((totalDeceased / total) * 100).toFixed(2);
        }
    }
    return "0";
};

const flattenedGroups = computed(() => {
    if (!activeTank.value?.groups) return [];

    const result = [];

    for (const group of activeTank.value.groups) {
        if (Array.isArray(group.progressData) && group.progressData.length > 0) {
            for (const progress of group.progressData) {
                result.push({
                    ...group,
                    progressData: progress
                });
            }
        }
    }

    return result.reverse(); // Newest first
});

const generateTagString = (tags: GroupTag[]) => {
    if (!tags) return '';

    return tags.map(tag =>
        `<span style="display:inline-block; margin: 0 5px 5px 0; font-size:12px; text-align:center; padding:5px;
     color:${tag.textColor || 'white'}; border-radius:5px; background-color:${tag.backgroundColor}">
       ${tag.title}
     </span>`
    ).join('');
};

// Remove last mortality record
const removeLastItem = () => {
    if (!activeTank.value?.groups) return;

    const groupsWithData = activeTank.value.groups.filter(g =>
        Array.isArray(g.progressData) && g.progressData.length > 0
    );

    if (groupsWithData.length === 0) return;

    // Find group with most recent entry
    let targetGroup = groupsWithData[0];

    for (const group of groupsWithData) {
        if (group.progressData.length > targetGroup.progressData.length) {
            targetGroup = group;
        }
    }

    targetGroup.progressData.pop();
};

const saveTank = () => {
    if (activeTank.value) {
        activeTank.value.status = "completed";
    }
    toggleOverview();
};


const toggleOverview = (): void => {
    overview.value = !overview.value;
};

const handleBackClick = (): void => {
    navigateToOverview();
};

const handleGroupClick = (group: TankGroup): void => {
    if (activeTank.value) {
        navigateToGroupView(activeTank.value.key, group.key);
    }
};
</script>

<template>
    <div class="h-full flex flex-col justify-between">
        <!-- Groups View -->
        <div v-if="!overview">
            <!-- Header -->
            <div>
                <h1 class="mb-5 py-2 text-3xl font-bold text-gray-700 border-b border-secondary">
                    {{ activeTank?.label }}
                </h1>
            </div>

            <!-- Groups grid -->
            <div class="wrapper grid grid-cols-2 gap-2 h-fit">
                <div
                    v-for="group in activeTank?.groups"
                    :key="group.key"
                    class="bg-primary p-3 cursor-pointer text-white shadow-md flex items-center justify-between"
                    @click="handleGroupClick(group)"
                >
                    <div class="flex-1">
                        <span class="flex flex-1 mb-2">
                          <strong>{{ group.name }}</strong>
                        </span>
                        <span
                            v-for="flag of group.tags"
                            :key="flag.title"
                            :style="{ background: flag.backgroundColor }"
                            class="border border-white inline-flex mr-2 mb-2 text-xs p-1 rounded-md font-bold"
                        >
                          {{ flag.title }}
                        </span>
                    </div>
                    <div class="flex flex-col text-right">
                        <span class="mb-2">
                          Total: <strong>{{ group.amountInTank || group.count }}</strong>
                          <span> / </span>
                          Dead:
                          <strong>{{ calculateDeceased(group.progressData) }}</strong>
                        </span>
                        <span>
                          Rate:
                          <strong>
                            {{ calculatePercentage(group.progressData, group.amountInTank || group.count) }}%
                          </strong>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Empty state for no groups -->
            <div v-if="!activeTank?.groups || activeTank.groups.length === 0" class="py-8 text-center text-gray-500">
                This tank has no groups.
            </div>
        </div>

        <!-- Overview View -->
        <div v-if="overview">
            <div class="flex my-5 items-center gap-3 border-b border-secondary">
                <h1 class="py-2 text-3xl font-bold text-gray-700">
                    {{ activeTank?.label }} - Overview
                </h1>
            </div>

            <!-- Mortality Records Table -->
            <div v-if="flattenedGroups.length > 0" class="flex flex-col gap-3">
                <table class="w-full bg-gray-50 table-auto">
                    <thead>
                    <tr>
                        <th class="px-6 py-3 text-left text-sm font-medium text-gray-500 capitalize tracking-wider w-10">#</th>
                        <th class="px-6 py-3 text-left text-sm font-medium text-gray-500 capitalize tracking-wider">Fish Group</th>
                        <th class="px-6 py-3 text-left text-sm font-medium text-gray-500 capitalize tracking-wider">Reason</th>
                        <th class="px-6 py-3 text-left text-sm font-medium text-gray-500 capitalize tracking-wider">Quantity</th>
                        <th class="px-6 py-3 text-left text-sm font-medium text-gray-500 capitalize tracking-wider">Comment</th>
                    </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(group, index) in flattenedGroups" :key="index">
                        <td class="border text-sm text-gray-600 p-4 border-[#e5e5e5] w-10">{{ index + 1 }}</td>
                        <td class="border text-sm text-gray-600 p-4 border-[#e5e5e5]" v-html="generateTagString(group.tags)"></td>
                        <td class="border text-sm text-gray-600 p-4 border-[#e5e5e5]">{{ group.progressData.reason }}</td>
                        <td class="border text-sm text-gray-600 p-4 border-[#e5e5e5]">{{ group.progressData.quantity }}</td>
                        <td class="border text-sm text-gray-600 p-4 border-[#e5e5e5]">{{ group.progressData.comment }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty state for overview -->
            <div v-else class="py-8 text-center text-gray-500">
                No mortality data has been recorded for this tank.
            </div>
        </div>

        <!-- Footer for Groups View -->
        <div v-if="!overview" class="mt-auto">
            <FooterActions>
                <template #start>
                    <button
                        class="w-full"
                        @click="toggleOverview"
                    >
                        Overview
                    </button>
                </template>
                <template #end>
                    <button
                        class="w-full"
                        @click="handleBackClick"
                    >
                        Back
                    </button>
                </template>
            </FooterActions>
        </div>

        <!-- Footer for Overview -->
        <div v-if="overview" class="mt-auto">
            <FooterActions>
                <template #start>
                    <button
                        class="w-full"
                        @click="removeLastItem"
                    >
                        Remove last
                    </button>
                </template>
                <template #middle>
                    <button
                        class="w-full"
                        @click="toggleOverview"
                    >
                        Back
                    </button>
                </template>
                <template #end>
                    <button
                        class="w-full"
                        @click="saveTank"
                    >
                        Save tank
                    </button>
                </template>
            </FooterActions>
        </div>
    </div>
</template>
